import './EmployeeModal.css';
import React, { useState, useEffect } from 'react';
import Modal from '../Modal';
import { useEmployees } from '../../contexts/EmployeeContext';
import { useDealership } from '../../contexts/DealershipContext';
import RemovalPrompt from './RemovalPrompt';

const initialFormState = {
  name: '',
  title: '',
  email: '',
  mainPhone: '',
  mainPhoneExt: '',
  mobilePhone: '',
  mobilePhoneExt: '',
  fax: ''
};

const validateEmail = (email) => {
  if (!email) return true; // Allow empty for secondary email
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

const validatePhone = (phone) => {
  if (!phone) return true; // Optional except for one required phone
  const stripped = phone.replace(/\D/g, '');
  return stripped.length >= 10 && stripped.length <= 11;
};

const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

// Function to combine phone and extension for API submission
const combinePhoneWithExt = (phone, phoneExt) => {
  if (!phone) return '';
  if (!phoneExt) return phone.replace(/\D/g, '');
  return `${phone.replace(/\D/g, '')} Ext. ${phoneExt}`;
};

// Function to extract phone and extension from combined format
const extractPhoneAndExt = (combinedPhone) => {
  if (!combinedPhone) return { phone: '', ext: '' };
  
  const extMatch = combinedPhone.match(/Ext\.\s*(\d+)/i);
  let ext = '';
  let phone = combinedPhone;
  
  if (extMatch) {
    ext = extMatch[1];
    phone = combinedPhone.replace(/\s*Ext\.\s*\d+/i, '');
  }
  
  return { phone, ext };
};

const EmployeeModal = ({ onClose, onEmployeeAdded, employee = null }) => {
  const { createEmployee, updateEmployee, archiveEmployee } = useEmployees();
  const { dealership } = useDealership();
  
  // Initialize form with employee data if it exists, otherwise use initialFormState
  const getInitialFormData = () => {
    if (!employee) return initialFormState;
    
    // Parse phone numbers to extract extensions if they exist in the combined format
    const mainPhoneData = extractPhoneAndExt(employee.mainPhone || '');
    const mobilePhoneData = extractPhoneAndExt(employee.mobilePhone || '');
    
    return {
      name: employee.name || '',
      title: employee.title || '',
      email: employee.email || '',
      mainPhone: formatPhoneNumber(mainPhoneData.phone) || '',
      mainPhoneExt: mainPhoneData.ext || '',
      mobilePhone: formatPhoneNumber(mobilePhoneData.phone) || '',
      mobilePhoneExt: mobilePhoneData.ext || '',
      fax: formatPhoneNumber(employee.fax || '') || ''
    };
  };

  const [formData, setFormData] = useState(getInitialFormData());
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showRemovalPrompt, setShowRemovalPrompt] = useState(false);

  // Refresh form data when employee prop changes
  useEffect(() => {
    setFormData(getInitialFormData());
  }, [employee]);

  const validateForm = () => {
    const newErrors = {};

    // Name validation with 30 char max
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > 30) {
      newErrors.name = 'Name must be 30 characters or less';
    }

    // Title validation with 40 char max
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 40) {
      newErrors.title = 'Title must be 40 characters or less';
    }

    // Email validation with 42 char max
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Valid email is required';
    } else if (formData.email.length > 42) {
      newErrors.email = 'Email must be 42 characters or less';
    }

    // Phone validation
    if (!formData.mainPhone && !formData.mobilePhone && !formData.fax) {
      newErrors.mainPhone = 'At least one phone number is required';
    }

    ['mainPhone', 'mobilePhone', 'fax'].forEach(phoneField => {
      if (formData[phoneField] && !validatePhone(formData[phoneField])) {
        newErrors[phoneField] = 'Invalid phone number format';
      }
    });

    // Extension validation - 1 Ext. Max
    ['mainPhoneExt', 'mobilePhoneExt'].forEach(extField => {
      if (formData[extField] && !/^\d{1,5}$/.test(formData[extField])) {
        newErrors[extField] = 'Extension must be 1-5 digits';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    const phoneFields = ['mainPhone', 'mobilePhone', 'fax'];
    if (phoneFields.includes(name)) {
      newValue = formatPhoneNumber(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!dealership) {
      setErrors(prev => ({
        ...prev,
        submit: 'No active dealership. Please create or select a dealership first.'
      }));
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Combine phone numbers with extensions for API submission
      const mainPhoneCombined = combinePhoneWithExt(formData.mainPhone, formData.mainPhoneExt);
      const mobilePhoneCombined = combinePhoneWithExt(formData.mobilePhone, formData.mobilePhoneExt);
      const fax = formData.fax ? formData.fax.replace(/\D/g, '') : '';
      
      const employeeData = {
        dealershipBac: dealership.bac,
        name: formData.name,
        title: formData.title,
        email: formData.email,
        mainPhone: mainPhoneCombined,
        mobilePhone: mobilePhoneCombined,
        fax
      };

      if (employee && employee.id) {
        // Update existing employee if we have an employee ID
        await updateEmployee(employee.id, employeeData);
      } else {
        // Create new employee
        await createEmployee(employeeData);
      }
      
      if (onEmployeeAdded) {
        onEmployeeAdded();
      } else {
        onClose();
      }
    } catch (err) {
      setErrors(prev => ({
        ...prev,
        submit: err.message || 'Failed to save employee'
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveClick = () => {
    setShowRemovalPrompt(true);
  };

  const confirmRemoval = async () => {
    setLoading(true);
    try {
      if (employee) {
        await archiveEmployee(employee.id);
        if (onEmployeeAdded) {
          onEmployeeAdded();
        } else {
          onClose();
        }
      }
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        submit: error.message || 'Failed to remove employee'
      }));
    } finally {
      setLoading(false);
      setShowRemovalPrompt(false);
    }
  };

  const cancelRemoval = () => {
    setShowRemovalPrompt(false);
  };

  if (showRemovalPrompt) {
    return <RemovalPrompt onConfirm={confirmRemoval} onCancel={cancelRemoval} />;
  }

  return (
    <Modal
      title={employee ? 'Edit Employee' : 'Add New Employee'}
      onClose={onClose}
      maxWidth={600}
    >
        {errors.submit && <div className="error-message">{errors.submit}</div>}

        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <div className="form-section">
            <div className="form-group">
              <label htmlFor="name">Full Name*</label>
              <div className="input-container">
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter employee's full name"
                  className={errors.name ? 'error' : ''}
                  maxLength={30}
                />
                <span className="char-limit">30 Characters Max</span>
              </div>
              {errors.name && <span className="error-text">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="title">Job Title*</label>
              <div className="input-container">
                <input
                  id="title"
                  name="title"
                  type="text"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Enter job title"
                  className={errors.title ? 'error' : ''}
                  maxLength={40}
                />
                <span className="char-limit">40 Characters Max</span>
              </div>
              {errors.title && <span className="error-text">{errors.title}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address*</label>
              <div className="input-container">
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  className={errors.email ? 'error' : ''}
                  maxLength={42}
                />
                <span className="char-limit">42 Characters Max</span>
              </div>
              {errors.email && <span className="error-text">{errors.email}</span>}
            </div>
          </div>

          {/* Phone Numbers Section */}
          <div className="form-section">
            <h3>Phone Numbers</h3>
            {errors.mainPhone && <div className="error-message">{errors.mainPhone}</div>}

            {/* Main Phone */}
            <div className="phone-group">
              <label>Main Phone</label>
              <div className="phone-input-group">
                <input
                  type="text"
                  name="mainPhone"
                  value={formData.mainPhone}
                  onChange={handleChange}
                  placeholder="(555) 555-5555"
                  className={errors.mainPhone ? 'error' : ''}
                />
                <input
                  name="mainPhoneExt"
                  type="text"
                  value={formData.mainPhoneExt}
                  onChange={handleChange}
                  placeholder="Ext."
                  className={`extension-input ${errors.mainPhoneExt ? 'error' : ''}`}
                />
              </div>
              {errors.mainPhone && <span className="error-text">{errors.mainPhone}</span>}
              {errors.mainPhoneExt && <span className="error-text">{errors.mainPhoneExt}</span>}
            </div>

            {/* Mobile Phone */}
            <div className="phone-group">
              <label>Mobile Phone</label>
              <div className="phone-input-group">
                <input
                  type="text"
                  name="mobilePhone"
                  value={formData.mobilePhone}
                  onChange={handleChange}
                  placeholder="(555) 555-5555"
                  className={errors.mobilePhone ? 'error' : ''}
                />
                <input
                  name="mobilePhoneExt"
                  type="text"
                  value={formData.mobilePhoneExt}
                  onChange={handleChange}
                  placeholder="Ext."
                  className={`extension-input ${errors.mobilePhoneExt ? 'error' : ''}`}
                />
              </div>
              {errors.mobilePhone && <span className="error-text">{errors.mobilePhone}</span>}
              {errors.mobilePhoneExt && <span className="error-text">{errors.mobilePhoneExt}</span>}
            </div>

            {/* Fax */}
            <div className="phone-group">
              <label>Fax</label>
              <div className="phone-input-group">
                <input
                  type="text"
                  name="fax"
                  value={formData.fax}
                  onChange={handleChange}
                  placeholder="(555) 555-5555"
                  className={errors.fax ? 'error' : ''}
                />
              </div>
              {errors.fax && <span className="error-text">{errors.fax}</span>}
            </div>
            
            <div className="phone-limit-note">
              <span>3 Phone numbers maximum</span>
              <span>Ext. optional for main/mobile</span>
            </div>
          </div>

          <div className="modal-actions">
            {employee && (
              <button
                type="button"
                className="btn btn-danger action-left"
                onClick={handleRemoveClick}
                disabled={loading}
              >
                Remove
              </button>
            )}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (employee ? 'Updating...' : 'Adding...') : 'Save'}
            </button>
          </div>
        </form>
    </Modal>
  );
};

export default EmployeeModal;
